import { Component, OnInit, ViewEncapsulation, ViewChild } from '@angular/core';
import { AuthorizationService } from '@app/services/authorization.service';
import { SocketService } from '@app/services/socket.service'
import { LocalStorageUtilities } from '@app/utilities/local-storage-utilities';
import { PersonPortalState } from '@app/models/person-portal-state';
import { MainScreenEvents } from '@app/interfaces/main-screen-events';
import { AccountValues } from '@app/models/account-values';
import { SpotPrice } from '@app/models/spot-price';
import { LatestPrices } from '@app/models/latest-prices';
import { HttpErrorResponse } from '@angular/common/http';
import {
  ChartComponent,
  ApexChart,
  ApexAxisChartSeries,
  ApexTitleSubtitle,
  ApexDataLabels,
  ApexFill,
  ApexYAxis,
  ApexXAxis,
  ApexTooltip,
  ApexMarkers,
  ApexAnnotations,
  ApexStroke
} from "ng-apexcharts";
import ApexCharts from 'apexcharts/dist/apexcharts.common.js'
import { DOCUMENT } from '@angular/common';



export type ChartOptions = {
  series: ApexAxisChartSeries;
  chart: ApexChart;
  dataLabels: ApexDataLabels;
  markers: ApexMarkers;
  title: ApexTitleSubtitle;
  fill: ApexFill;
  yaxis: ApexYAxis;
  xaxis: ApexXAxis;
  tooltip: ApexTooltip;
  stroke: ApexStroke;
  annotations: ApexAnnotations;
  colors: any;
  toolbar: any;
};

@Component({
  selector: 'app-summary',
  templateUrl: './summary.component.html',
  styleUrls: ['./summary.component.css'],
  encapsulation: ViewEncapsulation.None
})
export class SummaryComponent implements OnInit, MainScreenEvents {

  public personPortalState: PersonPortalState;
  // MXOLE
  public loanBalance: number = 0;
  public leaseProceedsBalance: number = 0;
  public leaseMVBalance: number = 0;
  public totalIndebtedness: number = 0;
  public longMVBalance: number = 0;
  public netAccountValue: number = 0;
  public equityPct: number = 0;
  // Prices
  public goldSpotPrice: number = 0;
  public goldOpenPrice: number = 0;
  public goldHighPrice: number = 0;
  public goldLowPrice: number = 0;
  public goldPriceChange: number = 0;
  public goldPctPriceChange: number = 0;
  public silverSpotPrice: number = 0;
  public silverOpenPrice: number = 0;
  public silverHighPrice: number = 0;
  public silverLowPrice: number = 0;
  public silverPriceChange: number = 0;
  public silverPctPriceChange: number = 0;
  public platinumSpotPrice: number = 0;
  public platinumOpenPrice: number = 0;
  public platinumHighPrice: number = 0;
  public platinumLowPrice: number = 0;
  public platinumPriceChange: number = 0;
  public platinumPctPriceChange: number = 0;
  public palladiumSpotPrice: number = 0;
  public palladiumOpenPrice: number = 0;
  public palladiumHighPrice: number = 0;
  public palladiumLowPrice: number = 0;
  public palladiumPriceChange: number = 0;
  public palladiumPctPriceChange: number = 0;
  // Styling.
  public goldChartOptions: Partial<ChartOptions>;
  public silverChartOptions: Partial<ChartOptions>;
  public platinumChartOptions: Partial<ChartOptions>;
  public palladiumChartOptions: Partial<ChartOptions>;
  // Data loaded indicators.
  public platinumChartModelLoaded: boolean = false;
  public palladiumChartModelLoaded: boolean = false;
  // Chart prices
  private goldPriceHistory: [number, number][];

  @ViewChild("goldApexChart", { static: false }) goldApexChart: ChartComponent;
  @ViewChild("silverApexChart", { static: false }) silverApexChart: ChartComponent;
  @ViewChild("platinumApexChart", { static: false }) platinumApexChart: ChartComponent;
  @ViewChild("palladiumApexChart", { static: false }) palladiumApexChart: ChartComponent;

  constructor(private authorizationService: AuthorizationService,
     private socketService: SocketService,) { }

  ngOnInit(): void {
    this.initChart();
    this.socketService.getMessageSubject().subscribe((prices: LatestPrices[]) => {
      this.updateSpotUIPrices(prices)
    });  
  }

  /**
   * Called from the "Main" module when the selected account changes.
   */
  public refreshUI(): void {
    this.initUI();
    this.onShowGoldChartModelLoadProgressPanel();
  }

  public initUI(): void {
    // Summary account values.
    this.refreshAccountValues();
    // Load table models.
    this.personPortalState = LocalStorageUtilities.getPersonPortalState();
    this.initializeSpotPrices();
  }

  /**
   * MXOLE
   * 
  * Refresh "Account Summary" values.
  */
  public refreshAccountValues(): void {
    let personPortalState: PersonPortalState = LocalStorageUtilities.getPersonPortalState();    
      this.authorizationService.getMxole(personPortalState.acctNum).subscribe({
      next: (accountValue: AccountValues) => {
        this.loanBalance = accountValue.loanBalance * -1;
        this.leaseProceedsBalance = accountValue.comLoanProceeds * -1;
        this.leaseMVBalance = accountValue.comLoanMV;
        this.totalIndebtedness = this.loanBalance + this.leaseProceedsBalance + this.leaseMVBalance;
        this.longMVBalance = accountValue.longMV;
        this.netAccountValue = this.totalIndebtedness + this.longMVBalance;
        this.equityPct = accountValue.equityPercent;
      },
      error: (err: HttpErrorResponse) => {
        // TODO: Implement!!!
      }
    });
  }

  private initChart(): void {
    this.initChartOptions();
  }

  // Set chart UI's.
  private initChartOptions(): void {
    this.goldChartOptions = this.getGoldChartOptions();
    this.silverChartOptions = this.getSilverChartOptions();
    this.platinumChartOptions = this.getPlatinumChartOptions();
    this.palladiumChartOptions = this.getPalladiumChartOptions();
  }

  public onTabChanged($event: any): void {
    let clickedIndex = $event.index;
    switch (clickedIndex) {
      case 0:
        this.onShowGoldChartModelLoadProgressPanel();
        break;
      case 1:
        this.onShowSilverChartModelLoadProgressPanel();
        break;
      case 2:
        this.onShowPlatinumChartModelLoadProgressPanel();
        break;
      case 3:
        this.onShowPalladiumChartModelLoadProgressPanel();
    }
  }

  private onShowGoldChartModelLoadProgressPanel(): void {
    if (this.goldPriceHistory == undefined) {
      // Get prices.
      this.authorizationService.findHistoricalGoldPrices().subscribe({
        next: (prices: [number, number][]) => {
          // Set history cache.
          this.goldPriceHistory = prices;
          /**
           * This serves to prevent the UI from freezing thus
           * tab transition as well as other UI actions occur
           * in a smooth fashion.
           */
          setTimeout(() => {
            this.goldApexChart.updateSeries([
              {
                name: "Monex Spot Gold",
                data: prices,
              }
            ]);
            this.annotateChart(this.goldApexChart, prices);
            this.addTodaysPriceAnnotation(this.goldApexChart, this.goldSpotPrice);
          },
            500);
        },
        error: (err: HttpErrorResponse) => {
          console.log(err);
        }
      });
    } else {
      /**
           * This serves to prevent the UI from freezing thus
           * tab transition as well as other UI actions occur
           * in a smooth fashion.
           */
      setTimeout(() => {
        this.goldApexChart.updateSeries([
          {
            name: "Monex Spot Gold",
            data: this.goldPriceHistory,
          }
        ]);
        this.annotateChart(this.goldApexChart, this.goldPriceHistory);
        this.addTodaysPriceAnnotation(this.goldApexChart, this.goldSpotPrice);
      },
        400);
    }
  }

  private onShowSilverChartModelLoadProgressPanel(): void {
    // Get prices.
    this.authorizationService.findHistoricalSilverPrices().subscribe({
      next: (prices: [number, number][]) => {
        setTimeout(() => {
          this.silverApexChart.updateSeries([
            {
              name: "Monex Spot Silver",
              data: prices,
            }
          ]);
          this.annotateChart(this.silverApexChart, prices);
          this.addTodaysPriceAnnotation(this.silverApexChart, this.silverSpotPrice);
        },
          500);
      },
      error: (err: HttpErrorResponse) => {
        console.log(err);
      }
    });
  }

  private onShowPlatinumChartModelLoadProgressPanel(): void {
    // Get prices.
    this.authorizationService.findHistoricalPlatinumPrices().subscribe({
      next: (prices: [number, number][]) => {
        setTimeout(() => {
          this.platinumApexChart.updateSeries([
            {
              name: "Monex Spot Platinum",
              data: prices,
            }
          ]);
          this.annotateChart(this.platinumApexChart, prices);
          this.addTodaysPriceAnnotation(this.platinumApexChart, this.platinumSpotPrice);
        },
          500);
      },
      error: (err: HttpErrorResponse) => {
        console.log(err);
      }
    });
  }

  private onShowPalladiumChartModelLoadProgressPanel(): void {
    // Get prices.
    this.authorizationService.findHistoricalPalladiumPrices().subscribe({
      next: (prices: [number, number][]) => {
        setTimeout(() => {
          this.palladiumApexChart.updateSeries([
            {
              name: "Monex Spot Palladium",
              data: prices,
            }
          ]);
          this.annotateChart(this.palladiumApexChart, prices);
          this.addTodaysPriceAnnotation(this.palladiumApexChart, this.palladiumSpotPrice);
        },
          500);
      },
      error: (err: HttpErrorResponse) => {
        console.log(err);
      }
    });
  }

  /**
     * This is used a startup for initializing spot prices. 
     */
  public initializeSpotPrices(): void {
    this.authorizationService.findSpotPrices().subscribe({
      next: (prices: SpotPrice[]) => {
        this.initSpotUIPrices(prices);
      },
      error: (err: HttpErrorResponse) => {
        console.log("Spot prices request error.");
      }
    });
  }

  /**
   * Spot prices displayed in commodity panels at start up.
   * 
   * @param prices 
   */
  public initSpotUIPrices(prices: SpotPrice[]) {
    prices.forEach((prices: SpotPrice) => {
      if (prices.symbol === "GBX") {
        this.goldSpotPrice = prices.last_ask;
        this.goldOpenPrice = prices.open_ask;
        this.goldHighPrice = prices.high_ask;
        this.goldLowPrice = prices.low_ask;
        this.goldPriceChange = this.goldSpotPrice - prices.prev_ask;
        this.goldPctPriceChange = (this.goldPriceChange / prices.prev_ask) * 100;
        // this.goldLastPriceChangeTime = prices.timestamp;
        // Chart
        this.addTodaysPriceAnnotation(this.goldApexChart, this.goldSpotPrice);
      } else if (prices.symbol === "SBSPOT") {
        this.silverSpotPrice = prices.last_ask;
        this.silverOpenPrice = prices.open_ask;
        this.silverHighPrice = prices.high_ask;
        this.silverLowPrice = prices.low_ask;
        this.silverPriceChange = this.silverSpotPrice - prices.prev_ask;
        this.silverPctPriceChange = (this.silverPriceChange / prices.prev_ask) * 100;
        // this.silverLastPriceChangeTime = prices.timestamp;
        // Chart
        this.addTodaysPriceAnnotation(this.silverApexChart, this.silverSpotPrice);
      } else if (prices.symbol === "PL1") {
        this.platinumSpotPrice = prices.last_ask;
        this.platinumOpenPrice = prices.open_ask;
        this.platinumHighPrice = prices.high_ask;
        this.platinumLowPrice = prices.low_ask;
        this.platinumPriceChange = this.platinumSpotPrice - prices.prev_ask;
        this.platinumPctPriceChange = (this.platinumPriceChange / prices.prev_ask) * 100;
        // this.platinumLastPriceChangeTime = prices.timestamp;
        // Chart
        this.addTodaysPriceAnnotation(this.platinumApexChart, this.platinumSpotPrice);
      } else if (prices.symbol === "PA") {
        this.palladiumSpotPrice = prices.last_ask;
        this.palladiumOpenPrice = prices.open_ask;
        this.palladiumHighPrice = prices.high_ask;
        this.palladiumLowPrice = prices.low_ask;
        this.palladiumPriceChange = this.palladiumSpotPrice - prices.prev_ask;
        this.palladiumPctPriceChange = (this.palladiumPriceChange / prices.prev_ask) * 100;
        // this.palladiumLastPriceChangeTime = prices.timestamp;
        // Chart
        this.addTodaysPriceAnnotation(this.palladiumApexChart, this.palladiumSpotPrice);
      }
    });
  }

  /**
   * Spot prices displayed in commodity panels on
   * socket event.
   * 
   * @param prices 
   */
  public updateSpotUIPrices(prices: LatestPrices[]) {
    prices.forEach((prices: LatestPrices) => {
      if (prices.commodity === "GBX") {
        this.goldSpotPrice = prices.lastAsk;
        this.goldOpenPrice = prices.openAsk;
        this.goldHighPrice = prices.highAsk;
        this.goldLowPrice = prices.lowAsk;
        this.goldPriceChange = prices.askChange;
        this.goldPctPriceChange = (prices.askChange / prices.prevAsk) * 100;
        // this.goldLastPriceChangeTime = prices.changeDate;
        // Chart
        this.addTodaysPriceAnnotation(this.goldApexChart, this.goldSpotPrice);
        // Update chart
      } else if (prices.commodity === "SBSPOT") {
        this.silverSpotPrice = prices.lastAsk;
        this.silverOpenPrice = prices.openAsk;
        this.silverHighPrice = prices.highAsk;
        this.silverLowPrice = prices.lowAsk;
        this.silverPriceChange = prices.askChange;
        this.silverPctPriceChange = (prices.askChange / prices.prevAsk) * 100;
        //this.silverLastPriceChangeTime = prices.changeDate;
        // Chart
        this.addTodaysPriceAnnotation(this.silverApexChart, this.silverSpotPrice);
      } else if (prices.commodity === "PL1") {
        this.platinumSpotPrice = prices.lastAsk;
        this.platinumOpenPrice = prices.openAsk;
        this.platinumHighPrice = prices.highAsk;
        this.platinumLowPrice = prices.lowAsk;
        this.platinumPriceChange = prices.askChange;
        this.platinumPctPriceChange = (prices.askChange / prices.prevAsk) * 100;
        //this.platinumLastPriceChangeTime = prices.changeDate;
        // Chart
        this.addTodaysPriceAnnotation(this.platinumApexChart, this.platinumSpotPrice);
      } else if (prices.commodity === "PA") {
        this.palladiumSpotPrice = prices.lastAsk;
        this.palladiumOpenPrice = prices.openAsk;
        this.palladiumHighPrice = prices.highAsk;
        this.palladiumLowPrice = prices.lowAsk;
        this.palladiumPriceChange = prices.askChange;
        this.palladiumPctPriceChange = (prices.askChange / prices.prevAsk) * 100;
        //this.palladiumLastPriceChangeTime = prices.changeDate;
        // Chart
        this.addTodaysPriceAnnotation(this.palladiumApexChart, this.palladiumSpotPrice);
      }
    });
  }

  /**
   * Chart styling options.
   */
  private getGoldChartOptions(): Partial<ChartOptions> {

    let goldOptions: Partial<ChartOptions> = {
      chart: {
        type: "area",
        height: 330,
        width: "100%",
        toolbar: {
          tools: {
            download: false,
          }
        }
      },
      colors: ['#cc7a00', '#e68a00', '#ff9900', '#ffa31a', '#ffad33'],
      series: [
        {
          name: "",
          data: [],
        }
      ],
      annotations: {
        yaxis: [
          {
            borderColor: "#999",
          }
        ],
        xaxis: [
          {
            borderColor: "#999",
          }
        ]
      },
      dataLabels: {
        enabled: false,
      },
      markers: {
        size: 0
      },
      xaxis: {
        type: "datetime",
        tickAmount: 6
      },
      tooltip: {
        x: {
          show: false,
          format: "MM/d/yyyy"
        },
      },
      stroke: {
        show: true,
        curve: 'smooth',
        lineCap: 'butt',
        colors: ['#D4AF37'],
        width: 1,
        dashArray: 0,
      },
      fill: {
        type: "gradient",
        gradient: {
          shadeIntensity: 1,
          opacityFrom: 0.7,
          opacityTo: 0.9,
          stops: [0, 100]
        }
      },
    };
    return goldOptions;
  }

  private getSilverChartOptions(): Partial<ChartOptions> {
    let silverOptions: Partial<ChartOptions> = {
      chart: {
        type: "area",
        height: 330,
        width: "100%",
        toolbar: {
          tools: {
            download: false,
          }
        }
      },
      colors: ['#c0c0c0', '#cccccc', '#d9d9d9', '#e6e6e6', '#f2f2f2'],
      series: [
        {
          name: "",
          data: [],
        }
      ],
      annotations: {
        yaxis: [
          {
            //y: 30,
            borderColor: "#999",
            // label: {
            //   text: "Support",
            //   style: {
            //     color: "#fff",
            //     background: "#00E396"
            //   }
            // }
          }
        ],
        xaxis: [
          {
            //x: new Date("14 Nov 2012").getTime(),
            borderColor: "#999",
            // label: {
            //   text: "Rally",
            //   style: {
            //     color: "#fff",
            //     background: "#775DD0"
            //   }
            // }
          }
        ]
      },
      dataLabels: {
        enabled: false,
      },
      markers: {
        size: 0
      },
      xaxis: {
        type: "datetime",
        tickAmount: 6
      },
      tooltip: {
        x: {
          show: false,
          format: "MM/d/yyyy"
        }
      },
      stroke: {
        show: true,
        curve: 'smooth',
        lineCap: 'butt',
        colors: undefined,
        width: 1,
        dashArray: 0,
      },
      fill: {
        type: "gradient",
        gradient: {
          shadeIntensity: 1,
          opacityFrom: 0.7,
          opacityTo: 0.9,
          stops: [0, 100]
        }
      }
    };
    return silverOptions;
  }

  private getPlatinumChartOptions(): Partial<ChartOptions> {
    let platinumOptions: Partial<ChartOptions> = {
      chart: {
        type: "area",
        height: 330,
        width: "100%",
        toolbar: {
          tools: {
            download: false,
          }
        }
      },
      colors: ['#9e9b94', '#aaa7a1', '#b6b4af', '#c2c0bc', '#cfcdc9', '#dbd9d7', '#e5e4e2', '#e7e6e4', '#f3f2f2'],
      series: [
        {
          name: "",
          data: [],
        }
      ],
      annotations: {
        yaxis: [
          {
            //y: 30,
            borderColor: "#999",
            // label: {
            //   text: "Support",
            //   style: {
            //     color: "#fff",
            //     background: "#00E396"
            //   }
            // }
          }
        ],
        xaxis: [
          {
            //x: new Date("14 Nov 2012").getTime(),
            borderColor: "#999",
            // label: {
            //   text: "Rally",
            //   style: {
            //     color: "#fff",
            //     background: "#775DD0"
            //   }
            // }
          }
        ]
      },
      dataLabels: {
        enabled: false,
      },
      markers: {
        size: 0
      },
      xaxis: {
        type: "datetime",
        tickAmount: 6
      },
      tooltip: {
        x: {
          show: false,
          format: "MM/d/yyyy"
        }
      },
      stroke: {
        show: true,
        curve: 'smooth',
        lineCap: 'butt',
        colors: undefined,
        width: 1,
        dashArray: 0,
      },
      fill: {
        type: "gradient",
        gradient: {
          shadeIntensity: 1,
          opacityFrom: 0.7,
          opacityTo: 0.9,
          stops: [0, 100]
        }
      }
    };
    return platinumOptions;
  }

  private getPalladiumChartOptions(): Partial<ChartOptions> {
    let palladiumOptions: Partial<ChartOptions> = {
      chart: {
        type: "area",
        height: 330,
        width: "100%",
        toolbar: {
          tools: {
            download: false,
          }
        }
      },
      colors: ['#868279', '#928e87', '#9e9b94', '#aaa7a1', '#b6b4af', '#c2c0bc', '#cfcdc9', '#dbd9d7', '#e5e4e2', '#e7e6e4', '#f3f2f2'],
      series: [
        {
          name: "",
          data: [],
        }
      ],
      annotations: {
        yaxis: [
          {
            //y: 30,
            borderColor: "#999",
            // label: {
            //   text: "Support",
            //   style: {
            //     color: "#fff",
            //     background: "#00E396"
            //   }
            // }
          }
        ],
        xaxis: [
          {
            //x: new Date("14 Nov 2012").getTime(),
            borderColor: "#999",
            // label: {
            //   text: "Rally",
            //   style: {
            //     color: "#fff",
            //     background: "#775DD0"
            //   }
            // }
          }
        ]
      },
      dataLabels: {
        enabled: false,
      },
      markers: {
        size: 0
      },
      xaxis: {
        type: "datetime",
        tickAmount: 6
      },
      tooltip: {
        x: {
          show: false,
          format: "MM/d/yyyy"
        }
      },
      stroke: {
        show: true,
        curve: 'smooth',
        lineCap: 'butt',
        colors: undefined,
        width: 1,
        dashArray: 0,
      },
      fill: {
        type: "gradient",
        gradient: {
          shadeIntensity: 1,
          opacityFrom: 0.7,
          opacityTo: 0.9,
          stops: [0, 100]
        }
      }
    };
    return palladiumOptions;
  }

  private annotateChart(chart: ChartComponent, data: [number, number][]) {
    if (chart) {
      // Get the timestamp for the day having the highest price in the data series.    
      let highTimeStamp: any = data.reduce((prev, current) => (prev[1] > current[1]) ? prev : current)
      chart.addXaxisAnnotation({
        x: highTimeStamp[0],
        borderColor: "#969696",
        label: {
          orientation: 'vertical',
          text: "High: $" + highTimeStamp[1],
          style: {
            color: "#ffffff",
            background: "#158000",
            fontSize: '12px',
          }
        }
      });
      // Get the timestamp for the day having the lowest price in the data series.  
      let lowTimeStamp: any = data.reduce((prev, current) => (prev[1] < current[1]) ? prev : current)
      chart.addXaxisAnnotation({
        x: lowTimeStamp[0],
        borderColor: "#969696",
        label: {
          orientation: 'vertical',
          text: "Low: $" + lowTimeStamp[1],
          style: {
            color: "#ffffff",
            background: "#d60404",
            fontSize: '12px',
          }
        }
      });
    }
  }

  /**
   * Adds the current price to the chart as an annotation.
   * 
   * Chart should not be null.
   * 
   * @param chart     
   * @param price 
   */
  private addTodaysPriceAnnotation(chart: ChartComponent, price: number) {
    let TODAYS_PRICE: string = "todays-price"
    chart?.removeAnnotation(TODAYS_PRICE)
    chart?.addYaxisAnnotation({
      id: TODAYS_PRICE,
      y: price,
      borderColor: "#000000",
      borderWidth: 1,
      label: {
        position: "left",
        offsetX: 65,
        text: "Today: $" + price,
        style: {
          color: "#000000",
          background: "#d4d4d4",
          fontSize: '12px',

        }
      }
    });
  }

}
